import { useState } from 'react';
import { BLOCKS, HERO } from './data';
import {
  subscribeErrorMessage,
  useLaunchSubscription,
  useSubscribeProductLaunchMutation,
} from '../../store/api/productLaunchApi';
import { t } from '../../i18n/i18n';
import './Divento.scss';

/**
 * The two card glyphs. Inlined rather than put through the app's `icon-mask`
 * idiom: both carry their own per-path opacity (a solid mark over a 25–30%
 * ghost of itself), and a mask flattens that to one flat colour.
 */
const ICONS = {
  course: (
    <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M11.652 8.568 8.001 6.439c-.333-.194-.752.046-.752.432v4.259c0 .386.419.626.752.432l3.651-2.129c.331-.193.331-.671 0-.864Z"
        fill="currentColor"
        fillOpacity=".3"
      />
      <path
        d="M11.652 8.568 8.001 6.439c-.333-.194-.752.046-.752.432v4.259c0 .386.419.626.752.432l3.651-2.129c.331-.193.331-.671 0-.864Z"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 1.75A7.25 7.25 0 0 1 16.25 9 7.25 7.25 0 0 1 9 16.25"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* The dotted half of the ring — eight marks rather than a dashed stroke,
          so the spacing stays even at any size. */}
      {[
        [3.873, 14.127],
        [1.75, 9],
        [3.873, 3.873],
        [6.226, 15.698],
        [2.302, 11.774],
        [2.302, 6.226],
        [6.226, 2.302],
      ].map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r=".75" fill="currentColor" />
      ))}
    </svg>
  ),
  certificate: (
    <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        clipRule="evenodd"
        fillRule="evenodd"
        d="M15.75 12.5v-1.333H13.5c-.859 0-1.556-.697-1.556-1.556 0-.859.697-1.556 1.556-1.556h2.25V6.722c0-.982-.796-1.778-1.778-1.778H4.278c-.982 0-1.778.796-1.778 1.778v6c0 .982.796 1.778 1.778 1.778h9.694c.982 0 1.778-.796 1.778-1.778Z"
        fill="currentColor"
        fillOpacity=".25"
      />
      <path
        d="M2.5 6.722c0-1.54 1.11-2.856 2.628-3.115l5.833-.996a.667.667 0 0 1 .945.479M15.75 11.167v1.333c0 .982-.796 1.778-1.778 1.778H4.278c-.982 0-1.778-.796-1.778-1.778V6.389c0-.982.796-1.778 1.778-1.778h9.694c.982 0 1.778.796 1.778 1.778v1.333"
        stroke="currentColor"
        strokeOpacity=".55"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.75 11.167h-2.25c-.859 0-1.556-.697-1.556-1.556 0-.859.697-1.556 1.556-1.556h2.25c.49 0 .889.398.889.889v.778c0 .49-.398.889-.889.889Z"
        stroke="currentColor"
        strokeOpacity=".55"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

/**
 * Divento Academy — 1:30762.
 *
 * A pre-launch page: a hero on the Divento fill with an email capture, over two
 * cards describing what is coming. The email goes to
 * `/product-launch/subscribe/` as `divento`.
 */
export default function Divento() {
  const [email, setEmail] = useState('');
  const [joined, setJoined] = useState(false);
  // Already on the list from an earlier visit, or just now.
  const subscription = useLaunchSubscription('divento');
  const onList = joined || Boolean(subscription);
  const [subscribe, { isLoading, error, reset }] = useSubscribeProductLaunchMutation();

  /* `type` and `required` give the browser's own format check first; the
     server's check is the one that decides. */
  const onSubmit = async (event) => {
    event.preventDefault();
    try {
      await subscribe({ product: 'divento', email: email.trim() }).unwrap();
      setJoined(true);
    } catch {
      // `error` from the hook carries it to the message under the form.
    }
  };

  return (
    <div className="divento">
      <section className="dv-hero">
        <div className="dv-hero__copy">
          <div className="dv-brand">
            <img className="dv-brand__mark" src="/images/divento-mark.svg" alt="" />
            <span className="dv-brand__name">{HERO.brand}</span>
            <span className="dv-chip">{t(HERO.chip)}</span>
          </div>

          <h1 className="dv-title">
            {HERO.titleLines.map((line, i) => (
              <span className="dv-title__line" key={line}>
                {t(line)}
                {i === 0 && <br />}
              </span>
            ))}
          </h1>

          <p className="dv-note">{t(HERO.note)}</p>

          {onList ? (
            <p className="dv-joined" role="status">
              <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="m3.5 8.5 3 3 6-7"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t('You’re on the list. We’ll let you know when Divento launches.')}
            </p>
          ) : (
          <form className="dv-notify" onSubmit={onSubmit}>
            <label className="dv-notify__field">
              <span className="dv-notify__label">{t('Email address')}</span>
              <input
                className="dv-notify__input"
                type="email"
                required
                placeholder={t(HERO.placeholder)}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) reset();
                }}
                disabled={isLoading}
                aria-invalid={Boolean(error)}
              />
            </label>

            <button className="dv-notify__submit" type="submit" disabled={isLoading}>
              {isLoading ? t('Sending…') : t(HERO.cta)}
              <svg className="dv-notify__arrow" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path
                  d="M2.5 6h7M6.75 3.25 9.5 6l-2.75 2.75"
                  stroke="currentColor"
                  strokeWidth="1.35"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </form>
          )}

          {error && !onList ? (
            <p className="dv-small dv-small--error" role="alert">
              {subscribeErrorMessage(error)}
            </p>
          ) : (
            <p className="dv-small">{t(HERO.small)}</p>
          )}
        </div>
      </section>

      <div className="dv-blocks">
        {BLOCKS.map((block) => (
          <section className="dv-block" key={block.id}>
            <span className="dv-block__icon">{ICONS[block.icon]}</span>
            <p className="dv-block__value">{t(block.value)}</p>
            <p className="dv-block__tag">{t(block.tag)}</p>

            <div className="dv-block__stats">
              {block.stats.map((stat) => (
                <span className="dv-block__stat" key={stat}>
                  {t(stat)}
                </span>
              ))}
            </div>

            <p className="dv-block__copy">{t(block.copy)}</p>
          </section>
        ))}
      </div>
    </div>
  );
}
